package com.example.mystybag.login.logic

import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.common.resources.SR
import com.example.mystybag.login.model.MerchantRegistrationPaymentDataUIState
import com.example.mystybag.login.utils.PaymentDataValidator
import com.example.mystybag.sharedmodels.project.requestModels.merchant.CheckMerchantIbanExistsRequestModel
import kotlinx.coroutines.launch

class MerchantRegistrationPaymentDataViewModel(
    context: AppContext,
    private val config: NavigatorMerchantRegistrationStep3
) : ChildViewModel<MerchantRegistrationPaymentDataUIState>(context, MerchantRegistrationPaymentDataUIState()) {

    private val sharedConfig: MerchantRegistrationSharedConfig
        get() = config.sharedConfig

    init {
        updateState {
            it.copy(
                iban = sharedConfig.iban
            )
        }
    }

    override fun onCreate() {
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.registration)
    }

    fun updateIban(value: String) {
        val filteredIban = value.filter { it.isLetterOrDigit() }.uppercase()

        updateState {
            it.copy(
                iban = filteredIban,
                ibanError = null
            )
        }
        sharedConfig.iban = value
    }
    
    override fun onBackButtonClicked() {
        // Update shared config with current state before going back
        val state = uiState.value
        sharedConfig.iban = state.iban
        
        // Update current config in stack
        val updatedConfig = config.copy(sharedConfig = sharedConfig)
        replaceLast(updatedConfig)
        
        super.onBackButtonClicked()
    }

    fun onContinue() {
        val state = uiState.value
        if (state.isLoading) return

        if (state.iban.isBlank()) {
            updateState { it.copy(ibanError = "required") }
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }

        val isIbanValid = PaymentDataValidator.isValidIban(state.iban)
        if (!isIbanValid) {
            updateState { it.copy(ibanError = "invalid_iban") }
            showSnackbar(sharedString(SR.strings.invalid_iban))
            return
        }

        updateState { it.copy(ibanError = null) }

        scope.launch {
            updateState { it.copy(isLoading = true) }

            commandBlock(showProgress = true) {
                CommonCommands.checkMerchantIbanExists(
                    CheckMerchantIbanExistsRequestModel(iban = state.iban)
                ).onResponse(
                    success = { response ->
                        launchOnMain {
                            if (response.isExistingMerchant) {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(sharedString(SR.strings.merchant_with_this_iban_exists))
                                return@launchOnMain
                            }

                            // Update shared config with actual data
                            sharedConfig.iban = state.iban

                            // Update current config in stack with updated shared config
                            val updatedConfig = config.copy(sharedConfig = sharedConfig)
                            replaceLast(updatedConfig)

                            updateState { it.copy(isLoading = false) }

                            // Navigate to Step 4 with the same shared config
                            navigate(NavigatorMerchantRegistrationStep4(sharedConfig = sharedConfig))
                        }
                    },
                    baseError = {
                        launchOnMain {
                            updateState { it.copy(isLoading = false) }
                        }
                    }
                )
            }
        }
    }
}
